import { isEmpty } from "lodash";
import locale from "@bonliva-crm/locale";
import { Search } from "@material-ui/icons";
import { FilterProps, FiltersPropsRecords } from "./filters-record-types";
import { AgeFilter } from "../filters/age-filter";
import { CallAndContactListFilter } from "../filters/call-and-contact-list-filter";
import { CanWorkInFilter } from "../filters/can-work-in-filter";
import { CanWorkInRegionsFilter } from "../filters/can-work-in-region-filter";
import { HasDrivingLicenseFilter } from "../filters/has-driving-license-filter";
import { HasEmailFilter } from "../filters/has-email-filter";
import { KnownSystemsFilter } from "../filters/known-systems-filter";
import { InitialsFirstNameFilter } from "../filters/initials-first-name-filter";
import { InitialsLastNameFilter } from "../filters/initials-last-name-filter";
import { PreferredScheduleFilter } from "../filters/preferred-schedule-filter";
import { ResponsiblePersonFilter } from "../filters/responsible-person-filter";
import { SearchProfileFilter } from "../filters/search-profile-filter";
import { WagesTypeFilter } from "../filters/wages-type-filter";
import { ZipCityFilter } from "../filters/zip-city-filter";
import { WorkedInNorwayFilter } from "../filters/worked-in-norway-filter";
import { HPRNumberFilter } from "../filters/hpr-number-filter";
import { QualificationFilter } from "../filters/qualification-filter";
import { NationalQualificationFilter } from "../filters/national-qualification-filter";
import { FitnessFilter } from "../filters/fitness-filter";
import { WorksViaCompetitorsFilter } from "../filters/works-via-competitors-filter";
import { InitialContactFilter } from "../filters/initial-contact-filter";
import { AvailabilityFilter } from "../filters/availability-filter";
import { GDPRMarketingFilter } from "../filters/gdpr-marketing-filter";
import { GDPRIntegrationPolicyFilter } from "../filters/gdpr-integration-policy-filter";
import { GDPRLegalBasisFilter } from "../filters/gdpr-legal-basis-filter";
import { LegalPersonTypeFilter } from "../filters/legal-person-type-filter";
import { HasPhoneFilter } from "../filters/has-phone-filter";
import { CustomersNameFilter } from "../filters/customers-name-filter";
import { BookingStatusFilter } from "../filters/booking-status-filter/booking-status-filter";
import { LastContactFilter } from "../filters/last-contact-filter";
import { ProfessionFilter } from "../filters/profession-filter/profession-filter";
import { LeadStatusFilter } from "../filters/lead-status-filter";
import { TagsFilter } from "../filters/tags-filter";
import { TextFieldController } from "@bonliva-crm/ui";
import { SortFilter } from "../filters/sort-filter";
import { isFilterSelected } from "./is-filter-selected";
import { QualityCheckFilter } from "../filters/quality-check-filter/quality-check-filter";
import { NextContactFilter } from "../filters/next-contact-filter";
import { SimpleProfessionFilter } from "../filters/simple-profession-filter/simple-profession-filter";
import { PipelineStatusFilter } from "../filters/pipeline-status-filter";
import { TeamsFilter } from "../filters/teams-filter";
import { OrgTypeFilter } from "../filters/org-type-filter";
import { GDPRJobMailingFilter } from "../filters/gdpr-job-mailing-filter";
import { MunicipalitiesFilter } from "../filters/municipalities-filter";
import { CustomerGroupsFilter } from "../filters/customer-groups-filter";
import { AoeFilter } from "../filters/aoe-filter";
import { CostCenterFilter } from "../filters/cost-center-filter";
import { ConsultantNameFilter } from "../filters/consultant-name-filter";
import { ProjectResponsiblePersonFilter } from "../filters/project-responsible-person-filter";
import { GroupInvoicingFilter } from "../filters/group-invoicing-filter";
import { IsNationalFilter } from "../filters/is-national-filter";
import { ManagedByFilter } from "../filters/managed-by-filter";
import { ConsultantPayoutFilter } from "../filters/consultant-payout-filter";
import { StatusFilter } from "../filters/status-filter";
import { BookingKitTagFilter } from "../filters/booking-kit-tag-filter";
import {
  accommodationRequestStatusOptions,
  bookingKitStatusOptions,
  travelRequestStatusOptions
} from "./options";
import { KitTypeFilter } from "../filters/kit-type-filter";

export type SearchFilterNames = keyof ReturnType<typeof getSearchComponentMap>;

export type BaseSearchParams = {
  label?: string;
};

const createSearchComponentData = <T extends React.ComponentType<any>>(
  component: T,
  params?: Partial<React.ComponentProps<T>>
): SearchComponentData<T> => ({ component, params: params ?? {} });

export type SearchComponentData<
  T extends React.ComponentType<any> = React.ComponentType<any>
> = {
  component: T;
  params: Partial<React.ComponentProps<T>>;
};

export const getSearchComponentMap = () => {
  return {
    ageRange: createSearchComponentData(AgeFilter, { label: locale.AgeType }),
    areaOfExpertise: createSearchComponentData(AoeFilter, {
      label: locale.AreaOfExpertise
    }),
    alphabetFilter: createSearchComponentData(InitialsFirstNameFilter, {
      label: locale.letterFirstName
    }),
    initialsLastName: createSearchComponentData(InitialsLastNameFilter, {
      label: locale.letterLastName
    }),
    availability: createSearchComponentData(AvailabilityFilter, {
      label: locale.Availability
    }),
    availableForWorkInCountries: createSearchComponentData(CanWorkInFilter, {
      label: locale["Can work in"]
    }),
    bookingStatus: createSearchComponentData(BookingStatusFilter, {
      label: locale.BookingStatus
    }),
    callListIds: createSearchComponentData(CallAndContactListFilter, {
      label: locale.callAndContactLists
    }),
    competitors: createSearchComponentData(WorksViaCompetitorsFilter, {
      label: locale["Works via competitors"]
    }),
    consultantPayout: createSearchComponentData(ConsultantPayoutFilter, {
      label: locale.payoutPeriod
    }),
    costCenter: createSearchComponentData(CostCenterFilter, { label: locale.CostCenter }),
    customerIds: createSearchComponentData(CustomersNameFilter, {
      label: locale.Customers
    }),
    consultantIds: createSearchComponentData(ConsultantNameFilter, {
      label: locale.Consultants
    }),
    createdFromTo: createSearchComponentData(AvailabilityFilter, {
      label: locale.creationDate
    }),
    customerGroups: createSearchComponentData(CustomerGroupsFilter, {
      label: locale["Customer type"]
    }),
    fitness: createSearchComponentData(FitnessFilter, { label: locale.Fitness }),
    gdprIntegrationPolicy: createSearchComponentData(GDPRIntegrationPolicyFilter, {
      label: `${locale.GDPR}: ${locale.gdprConsentType.IntegrationPolicy}`
    }),
    gdprMarketing: createSearchComponentData(GDPRMarketingFilter, {
      label: `${locale.GDPR}: ${locale.gdprConsentType.Marketing}`
    }),
    gdprJobMailingEmail: createSearchComponentData(GDPRJobMailingFilter, {
      label: `${locale.GDPR}: ${locale.gdprConsentType.JobMailingEmail}`
    }),
    gdprJobMailingSms: createSearchComponentData(GDPRJobMailingFilter, {
      label: `${locale.GDPR}: ${locale.gdprConsentType.JobMailingSms}`
    }),
    groupInvoicing: createSearchComponentData(GroupInvoicingFilter, {
      label: locale.bookingSupportChecks.groupInvoicing
    }),
    isNational: createSearchComponentData(IsNationalFilter, {
      label: locale.NationalProcurement
    }),
    hasDrivingLicence: createSearchComponentData(HasDrivingLicenseFilter, {
      label: locale.HasDrivingLicence
    }),
    hasEmail: createSearchComponentData(HasEmailFilter, { label: locale.hasEmail }),
    hasLegalPersonType: createSearchComponentData(LegalPersonTypeFilter, {
      label: locale["Employment type"]
    }),
    hasPhone: createSearchComponentData(HasPhoneFilter, { label: locale.hasPhone }),
    hprNumber: createSearchComponentData(HPRNumberFilter, { label: locale.HPRNumber }),
    initialContact: createSearchComponentData(InitialContactFilter, {
      label: locale.InitialContact
    }),
    knownSystems: createSearchComponentData(KnownSystemsFilter, {
      label: locale["Known systems"]
    }),
    lastContact: createSearchComponentData(LastContactFilter, {
      label: locale.LastContactDate
    }),
    leadStatus: createSearchComponentData(LeadStatusFilter, { label: locale.leadStatus }),
    pipelineStatus: createSearchComponentData(PipelineStatusFilter, {
      label: locale.pipelineStatus
    }),
    legalBasis: createSearchComponentData(GDPRLegalBasisFilter, {
      label: `${locale.GDPR}: ${locale.LegalBasis}`
    }),
    location: createSearchComponentData(ZipCityFilter, {
      label: `${locale.ZipCode}/${locale.city2}`
    }),
    nationalQualification: createSearchComponentData(NationalQualificationFilter, {
      label: locale.NationalQualificationFilter
    }),
    kitStatusFilter: createSearchComponentData(StatusFilter, {
      label: locale.kitTableColumnType.KitStatus,
      options: bookingKitStatusOptions
    }),
    accommodationRequestStatusFilter: createSearchComponentData(StatusFilter, {
      label: locale.accommodationRequestTableColumnType.Status,
      options: accommodationRequestStatusOptions
    }),
    travelRequestStatusFilter: createSearchComponentData(StatusFilter, {
      label: locale.travelRequestTableColumns.Status,
      options: travelRequestStatusOptions
    }),
    organizationType: createSearchComponentData(OrgTypeFilter, {
      label: locale.OrganizationType
    }),
    profession: createSearchComponentData(ProfessionFilter, { label: locale.Profession }),
    simpleProfession: createSearchComponentData(SimpleProfessionFilter, {
      label: locale.Profession
    }),
    qualification: createSearchComponentData(QualificationFilter, {
      label: locale.QualificationFilter
    }),
    regions: createSearchComponentData(CanWorkInRegionsFilter, {
      label: locale.CanWorkInRegion
    }),
    responsiblePerson: createSearchComponentData(ResponsiblePersonFilter, {
      label: locale["Responsible BA"]
    }),
    projectResponsiblePersonId: createSearchComponentData(
      ProjectResponsiblePersonFilter,
      {
        label: locale.AllProjectsFor
      }
    ),
    managedBy: createSearchComponentData(ManagedByFilter, { label: locale.managedBy }),
    senderIds: createSearchComponentData(ManagedByFilter, { label: locale.sender }),
    searchProfile: createSearchComponentData(SearchProfileFilter, {
      label: locale.SearchProfiles
    }),
    sort: createSearchComponentData(SortFilter),
    text: createSearchComponentData(TextFieldController, {
      label: "",
      textFieldProps: {
        variant: "standard",
        placeholder: locale.Search,
        inputProps: {
          style: {
            paddingLeft: 10
          }
        },
        InputProps: {
          disableUnderline: true,
          startAdornment: <Search />
        }
      }
    }),
    wagesRange: createSearchComponentData(WagesTypeFilter, { label: locale.WagesType }),
    workedInNorway: createSearchComponentData(WorkedInNorwayFilter, {
      label: locale.WorkedInNorway
    }),
    workshifts: createSearchComponentData(PreferredScheduleFilter, {
      label: locale.PreferredSchedule
    }),
    qualityCheck: createSearchComponentData(QualityCheckFilter, {
      label: locale.QualityCheckDocument
    }),
    tags: createSearchComponentData(TagsFilter, { label: locale.tags }),
    bookingKitTagFilter: createSearchComponentData(BookingKitTagFilter, {
      label: locale.kitTableColumnType.ManagementStatus
    }),
    teams: createSearchComponentData(TeamsFilter, { label: locale.Team }),
    nextContact: createSearchComponentData(NextContactFilter, {
      label: locale.NextContact
    }),
    receivedAt: createSearchComponentData(AvailabilityFilter, {
      label: locale.ReceivedOn
    }),
    lastApplicationDate: createSearchComponentData(AvailabilityFilter, {
      label: locale.LastApplicationDate
    }),
    period: createSearchComponentData(AvailabilityFilter, { label: locale.Period }),
    municipalities: createSearchComponentData(MunicipalitiesFilter, {
      label: locale.municipalities
    }),
    bookingDate: createSearchComponentData(AvailabilityFilter, {
      label: locale.Week
    }),
    kitTypes: createSearchComponentData(KitTypeFilter, {
      label: locale.Type
    })
  } as const;
};

export const filterPropsFactory = ({
  filters,
  favoriteFilters,
  overrides,
  searchProfiles,
  setSearchProfiles,
  setSearchProfileFull,
  withFavourite,
  setFavoriteFilters,
  setSearchProfileModalProps,
  searchProfileType
}: FilterProps): FiltersPropsRecords => {
  const SearchComponentMap = getSearchComponentMap();

  return filters.reduce<FiltersPropsRecords>((acc, key) => {
    if (!SearchComponentMap[key]) {
      console.warn(`Filter ${key} is not defined in SearchComponentMap`);
      return acc;
    }

    const favProps = withFavourite
      ? {
          favorite: favoriteFilters.includes(key),
          setFavorite: (value: any) => {
            const prevVal = favoriteFilters ?? [];
            if (value) {
              setFavoriteFilters([...prevVal, key]);
            } else {
              setFavoriteFilters(prevVal.filter((v) => v !== key));
            }
          }
        }
      : {};

    if (key === "searchProfile") {
      acc[key] = {
        name: key,
        type: searchProfileType,
        value: searchProfiles,
        onChange: setSearchProfiles,
        resetFilter: () => {
          setSearchProfileFull(null);
          setSearchProfiles(null);
        },
        onOptionEdit: (option) => {
          setSearchProfileModalProps({
            open: true,
            mode: "edit",
            currentSearchProfile: option?.value
          });
        },
        ...favProps,
        ...(overrides?.[key] ? overrides[key] : {})
      };
    } else {
      acc[key] = { name: key, ...favProps, ...(overrides?.[key] ? overrides[key] : {}) };
    }

    return acc;
  }, {} as FiltersPropsRecords);
};

export function getIsSelected(
  values: any,
  searchProfile: any
): Record<SearchFilterNames, boolean> {
  return {
    text: !!values.text,
    sort: !!values.sort,
    responsiblePerson: isFilterSelected(values.responsiblePerson),
    projectResponsiblePersonId: isFilterSelected(values.projectResponsiblePersonId),
    managedBy: isFilterSelected(values.managedBy),
    senderIds: isFilterSelected(values.managedBy),
    hasEmail: isFilterSelected(values.hasEmail),
    leadStatus: isFilterSelected(values.leadStatus),
    kitStatusFilter: !isEmpty(values.kitStatusFilter),
    accommodationRequestStatusFilter: !isEmpty(values.accommodationRequestStatusFilter),
    travelRequestStatusFilter: !isEmpty(values.travelRequestStatusFilter),
    callListIds: !isEmpty(values.callListIds),
    alphabetFilter: !isEmpty(values.alphabetFilter),
    areaOfExpertise: !isEmpty(values.areaOfExpertise),
    initialsLastName: !isEmpty(values.initialsLastName),
    availableForWorkInCountries: !isEmpty(values.availableForWorkInCountries),
    regions: !isEmpty(values.regions),
    location: !!values.location?.zipOrCity,
    hasDrivingLicence: values.hasDrivingLicence !== "none",
    workshifts: !isEmpty(values.workshifts),
    knownSystems: !isEmpty(values.knownSystems),
    ageRange: !!values?.ageRange?.from && !!values?.ageRange?.to,
    wagesRange: !!values?.wagesRange?.from || !!values?.wagesRange?.to,
    workedInNorway: isFilterSelected(values.workedInNorway),
    hprNumber: isFilterSelected(values.hprNumber),
    qualification: isFilterSelected(values?.qualification),
    nationalQualification: isFilterSelected(values?.nationalQualification),
    fitness: isFilterSelected(values?.fitness),
    competitors: !isEmpty(values.competitors),
    consultantPayout: !isEmpty(values?.consultantPayout),
    costCenter: !isEmpty(values.costCenter),
    initialContact: !isEmpty(values.initialContact),
    availability: !!values?.availability?.from && !!values?.availability?.to,
    createdFromTo: !!values?.createdFromTo?.from && !!values?.createdFromTo?.to,
    gdprMarketing: isFilterSelected(values?.gdprMarketing),
    gdprJobMailingEmail: isFilterSelected(values?.gdprJobMailingEmail),
    gdprJobMailingSms: isFilterSelected(values?.gdprJobMailingSms),
    groupInvoicing: isFilterSelected(values?.groupInvoicing),
    isNational: isFilterSelected(values?.isNational),
    gdprIntegrationPolicy: isFilterSelected(values?.gdprIntegrationPolicy),
    legalBasis: isFilterSelected(values?.legalBasis),
    hasLegalPersonType: isFilterSelected(values?.hasLegalPersonType),
    hasPhone: isFilterSelected(values.hasPhone),
    customerIds: !isEmpty(values.customerIds),
    consultantIds: !isEmpty(values.consultantIds),
    customerGroups: !isEmpty(values.customerGroups),
    bookingStatus: isFilterSelected(values.bookingStatus),
    lastContact:
      !!values?.lastContact?.dates?.from ||
      !!values?.lastContact?.dates?.to ||
      values.lastContact?.type?.length > 0 ||
      !!values?.lastContact?.myActivity,
    searchProfile: searchProfile !== null,
    profession: values.profession?.professions.length > 0,
    simpleProfession: values.simpleProfession?.professions.length > 0,
    qualityCheck: values.qualityCheck?.qualityCheckStatuses.length > 0,
    tags: !isEmpty(values.tags),
    nextContact: values.nextContact?.from != null || values.nextContact?.to != null,
    pipelineStatus: !isEmpty(values.pipelineStatus),
    receivedAt: !!values?.receivedAt?.from && !!values?.receivedAt?.to,
    teams: !isEmpty(values.teams),
    organizationType: !isEmpty(values.organizationType),
    period: !!values?.period?.from && !!values?.period?.to,
    municipalities: !isEmpty(values.municipalities),
    bookingKitTagFilter: !isEmpty(values.bookingKitTagFilter),
    lastApplicationDate:
      !!values?.lastApplicationDate?.from && !!values?.lastApplicationDate.to,
    bookingDate: !!values?.bookingDate?.from && !!values?.bookingDate?.to,
    kitTypes: !isEmpty(values.kitTypes)
  };
}
